import { GAEvent, snowplowEvent } from './analytics';
import { getAdditionalParams } from './analyticsValuations';

export const HOMEPAGE_SECTIONS = {
  CLAIM: 'Homepage Claim',
  GUIDES: 'Homepage Guides',
  HERO: 'Homepage Hero',
  HOW_IT_WORKS: 'Homepage How It Works',
  NEWS_AND_TOOLS: 'Homepage What\'s New',
  RECOMMENDED_BY: 'Homepage Recommended By',
  REVIEWS: 'Homepage Reviews',
  SOCIAL_PROOF: 'Homepage Social Proof',
} as const;

export const LANDINGPAGE_SECTIONS = {
  HERO: 'Landing Page Hero',
} as const;

type SECTION_VALUES = typeof HOMEPAGE_SECTIONS[keyof typeof HOMEPAGE_SECTIONS] |
typeof LANDINGPAGE_SECTIONS[keyof typeof LANDINGPAGE_SECTIONS];

const EVENT_ACTIONS = {
  CTA_CLICKED: 'CTA Clicked',
  GUIDE_CLICKED: 'Guide clicked',
  GUIDES_CAROUSEL_SCROLLED: 'Guides carousel scrolled',
  GUIDES_CTA_CLICKED: 'Explore guides CTA clicked',
  HERO_MILEAGE_CTA_CLICKED: 'Continue CTA clicked',
  HERO_VRM_CTA_CLICKED: 'Value my car CTA clicked',
  HERO_VRM_INPUT_FOCUSSED: 'VRM input focussed',
  NEWS_AND_TOOLS_TILE_CLICKED: 'Tile clicked',
  SECTION_LOADED: 'Section Loaded',
} as const;

export const HOMEPAGE_GA_TAGS = {
  GUIDE_CLICKED: (guideTitle: string) => GAEvent({
    eventAction: EVENT_ACTIONS.GUIDE_CLICKED,
    eventCategory: HOMEPAGE_SECTIONS.GUIDES,
    eventLabel: guideTitle,
  }),
  GUIDES_EXPLORE_CLICKED: () => GAEvent({
    eventAction: EVENT_ACTIONS.GUIDES_CTA_CLICKED,
    eventCategory: HOMEPAGE_SECTIONS.GUIDES,
  }),
  GUIDES_SCROLLED: (guideTitle: string) => GAEvent({
    eventAction: EVENT_ACTIONS.GUIDES_CAROUSEL_SCROLLED,
    eventCategory: HOMEPAGE_SECTIONS.GUIDES,
    eventLabel: guideTitle,
  }),

  HERO_INVALID_VRM_WARNING_SHOWN: (vrm: string) => {
    GAEvent({
      eventAction: EVENT_ACTIONS.HERO_VRM_CTA_CLICKED,
      eventCategory: HOMEPAGE_SECTIONS.HERO,
      eventLabel: vrm ? 'Invalid VRM warning displayed' : 'No VRM',
    });
    snowplowEvent({
      data: { vrm },
      schema: 'smv_upperfunnel_vrm_submit_error',
    });
  },

  HERO_MILEAGE_CTA_CLICKED: (isHomepage = false) => GAEvent({
    eventAction: EVENT_ACTIONS.HERO_MILEAGE_CTA_CLICKED,
    eventCategory: isHomepage ? HOMEPAGE_SECTIONS.HERO : LANDINGPAGE_SECTIONS.HERO,
    eventLabel: 'Mileage input submitted',
  }),

  HERO_MILEAGE_INPUT_EMPTY: (isHomepage = false) => GAEvent({
    eventAction: EVENT_ACTIONS.HERO_MILEAGE_CTA_CLICKED,
    eventCategory: isHomepage ? HOMEPAGE_SECTIONS.HERO : LANDINGPAGE_SECTIONS.HERO,
    eventLabel: 'Mileage input empty',
  }),

  HERO_MILEAGE_SHOWN: (vrm: string, isHomepage = false) => {
    GAEvent({
      eventAction: 'Mileage input is in view',
      eventCategory: isHomepage ? HOMEPAGE_SECTIONS.HERO : LANDINGPAGE_SECTIONS.HERO,
    });
    snowplowEvent({
      ...getAdditionalParams({ vrm }),
      schema: 'smv_upperfunnel_mileage_arrival',
    });
  },

  HERO_VRM_CTA_CLICKED: () => {
    GAEvent({
      eventAction: EVENT_ACTIONS.HERO_VRM_CTA_CLICKED,
      eventCategory: HOMEPAGE_SECTIONS.HERO,
      eventLabel: 'VRM added',
    });
  },

  HERO_VRM_INPUT_FOCUSSED: () => GAEvent({
    eventAction: EVENT_ACTIONS.HERO_VRM_INPUT_FOCUSSED,
    eventCategory: HOMEPAGE_SECTIONS.HERO,
  }),

  NEWS_AND_TOOLS_LOADED: (label?: string) => GAEvent({
    eventAction: EVENT_ACTIONS.SECTION_LOADED,
    eventCategory: HOMEPAGE_SECTIONS.NEWS_AND_TOOLS,
    eventLabel: label,
  }),

  NEWS_AND_TOOLS_TILE_CLICKED: (label: string) => GAEvent({
    eventAction: EVENT_ACTIONS.NEWS_AND_TOOLS_TILE_CLICKED,
    eventCategory: HOMEPAGE_SECTIONS.NEWS_AND_TOOLS,
    eventLabel: label,
  }),

  SECTION_CTA_CLICKED: (category: SECTION_VALUES) => GAEvent({
    eventAction: EVENT_ACTIONS.CTA_CLICKED,
    eventCategory: category,
  }),

  SECTION_LOADED: (category: SECTION_VALUES, label?: string) => GAEvent({
    eventAction: EVENT_ACTIONS.SECTION_LOADED,
    eventCategory: category,
    eventLabel: label,
  }),
} as const;
